import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/landing/Reveal";
import { api, euro, formatApiError } from "@/lib/api";

const PackageCard = ({ pkg, onBuy, loading }) => {
  const dark = pkg.featured;
  return (
    <div data-testid={`pricing-card-${pkg.lookup_key}`}
      className={`relative flex h-full flex-col rounded-2xl border p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg ${dark ? "border-[#1E1A17] bg-[#1E1A17] text-[#FAF7F2]" : "border-[#E8E1D7] bg-white text-[#1E1A17]"}`}>
      {dark && <span className="absolute -top-3 left-8 rounded-full bg-[#F4A261] px-3 py-1 text-xs font-bold text-[#1E1A17]">El más elegido</span>}
      <h3 className="text-2xl font-bold">{pkg.name}</h3>
      <p className={`mt-1 text-sm ${dark ? "text-[#FAF7F2]/70" : "text-[#6E655F]"}`}>{pkg.subtitle}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <span data-testid={`pricing-amount-${pkg.lookup_key}`} className="font-display text-5xl font-bold">{euro(pkg.amount / 100)}</span>
        <span className={`text-sm ${dark ? "text-[#FAF7F2]/60" : "text-[#6E655F]"}`}>pago único</span>
      </div>
      <ul className="mt-8 flex-1 space-y-3">
        {pkg.features.map((f) => (
          <li key={f} className="flex gap-3 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2A9D8F]" />{f}</li>
        ))}
      </ul>
      <Button onClick={() => onBuy(pkg.lookup_key)} disabled={loading === pkg.lookup_key} data-testid={`pricing-buy-${pkg.lookup_key}`}
        className={`mt-8 h-12 rounded-full text-base active:scale-[0.98] ${dark ? "bg-[#E05A47] hover:bg-[#c94a38]" : "bg-[#1E1A17] hover:bg-[#E05A47]"}`}>
        {loading === pkg.lookup_key ? "Redirigiendo…" : "Contratar ahora"}
      </Button>
    </div>
  );
};

export const Pricing = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)); }, []);

  const buy = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <section id="paquetes" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#E05A47]">Paquetes</p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#1E1A17] sm:text-4xl">Precios claros, resultados reales</h2>
        <p className="mt-4 text-base text-[#6E655F] md:text-lg">Elige tu paquete y paga con tarjeta de forma segura. Empezamos en 48 horas.</p>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {packages.map((p, i) => (
          <Reveal key={p.lookup_key} delay={i * 0.08}><PackageCard pkg={p} onBuy={buy} loading={loading} /></Reveal>
        ))}
      </div>
      <p className="mt-8 text-sm text-[#6E655F]">¿Necesitas algo a medida? <a href="#contacto" className="font-semibold text-[#E05A47] underline-offset-4 hover:underline" data-testid="pricing-custom-quote-link">Pide una cotización personalizada</a>.</p>
    </section>
  );
};
